import logger from '../../utils/logger';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import '../../css/Login.css'; // Import the CSS file for styling
import { loginUser, setUser } from '../../redux/userSlice';
import { CurrentUser, googleOAuth, microsoftOAuth, loginVerifyTwoFactor } from '../../api/api';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import config from '../../config/config';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../config/msalConfig';
import { FaEye, FaEyeSlash, FaGoogle } from 'react-icons/fa'; // 👁️ Import eye and Google icons
import { useTranslation } from '../../hooks/useTranslation';
import Footer from '../../components/Footer';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); // 👁️ password toggle state

  const [error, setError] = useState('');
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  const t = useTranslation();

  const [hasFunctionalConsent, setHasFunctionalConsent] = useState(false);

  useEffect(() => {
    const checkConsent = () => {
      try {
        const savedConsent = localStorage.getItem('rocketbpm_cookie_consent');
        if (savedConsent) {
          const parsed = JSON.parse(savedConsent);
          setHasFunctionalConsent(!!parsed.functional);
        } else {
          setHasFunctionalConsent(false);
        }
      } catch (e) {
        setHasFunctionalConsent(false);
      }
    };

    checkConsent();
    window.addEventListener('cookie-consent-updated', checkConsent);
    return () => {
      window.removeEventListener('cookie-consent-updated', checkConsent);
    };
  }, []);

  const queryParams = new URLSearchParams(window.location.search);
  const redirectPath = queryParams.get('redirect');

  const from =
    redirectPath ||
    (location.state?.from?.pathname
      ? location.state.from.pathname + (location.state.from.search || '')
      : '/dashboard');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);
  const handleMicrosoftLogin = async () => {
    try {
      const loginResponse = await instance.loginPopup(loginRequest);
      const account = loginResponse.account;

      // Acquire token silently
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });

      const accessToken = tokenResponse.accessToken;

      // Send token to backend
      const response = await microsoftOAuth(accessToken); // API call
      dispatch(setUser(response.user));
      navigate(from, { replace: true });
    } catch (error) {
      logger.error('Microsoft login failed', error);
      setError(t('microsoft_signin_failed'));
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      const tokenId = credentialResponse.credential;
      const data = await googleOAuth(tokenId);
      dispatch(setUser(data.user));
      navigate(from, { replace: true });
    } catch (err) {
      logger.error(err);
      setError(err.message || t('google_signin_failed'));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await dispatch(loginUser({ email, password })).unwrap();
      if (response && response.two_factor_required) {
        setTempToken(response.temp_token);
        setTwoFactorRequired(true);
        setError('');
        return;
      }

      // Fetch current user details
      const userDetails = await CurrentUser();
      dispatch(setUser(userDetails.user || userDetails)); // Store user in Redux

      // Navigate to dashboard after setting user
      navigate(from, { replace: true }); // Go to original path
    } catch (err) {
      logger.error('login error', err);
      const msgKey = (typeof err === 'object' ? err?.message : err) || 'invalid_credentials';
      setError(t(msgKey));
    }
  };

  const handleTwoFactorVerify = async (e) => {
    e.preventDefault();
    if (!otpCode) return;
    try {
      setOtpLoading(true);
      setError('');
      const response = await loginVerifyTwoFactor({
        temp_token: tempToken,
        code: otpCode,
      });

      dispatch(setUser(response.user || response));

      // Fetch current user details to make sure Redux is fully in sync
      const userDetails = await CurrentUser();
      dispatch(setUser(userDetails.user || userDetails));

      navigate(from, { replace: true });
    } catch (err) {
      logger.error('2fa verify error', err);
      const msg =
        err?.response?.data?.errors?.code?.[0] ||
        err?.response?.data?.message ||
        t('invalid_verification_code');
      setError(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div
      className="login-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          flex: '1 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div className="login-wrapper">
          <div className="login-image">
            <img src="../../img/RocketBPM_rocket_logo.png" alt="Logo" className="login-logo" />
          </div>

          {twoFactorRequired ? (
            <>
              <h2>{t('two_factor_auth')}</h2>
              <form onSubmit={handleTwoFactorVerify} className="login-form">
                <p
                  style={{
                    fontSize: '0.875rem',
                    color: '#666',
                    marginBottom: '1.5rem',
                    textAlign: 'center',
                  }}
                >
                  {t('two_factor_auth_desc')}
                </p>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  placeholder={t('otp_placeholder')}
                  className="login-input"
                  required
                  autoComplete="one-time-code"
                />
                {error && <p className="error-message">{error}</p>}
                <button type="submit" disabled={otpLoading} className="login-button">
                  {otpLoading ? t('verifying') : t('verify')}
                </button>
                <div>
                  <button
                    type="button"
                    className="btn_form"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#002060',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setTwoFactorRequired(false);
                      setError('');
                    }}
                  >
                    {t('back_to_login')}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h2>{t('log_in_to_your_account')}</h2>
              <form onSubmit={handleLogin} className="login-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('Email')}
                  className="login-input"
                />
                <div className="password-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('password')}
                    className="login-input password-input"
                  />
                  <button
                    type="button"
                    className="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {error && <p className="error-message">{error}</p>}
                <button type="submit" className="login-button">
                  {t('sign_in')}
                </button>
                <p>
                  {t('not_a_member')}
                  <button
                    className="btn_form"
                    type="button"
                    onClick={() => navigate('/signup-form')}
                  >
                    {t('sign_up')}
                  </button>
                </p>
                <button
                  className="btn_form"
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                >
                  {t('forgot_your_password')}
                </button>
              </form>

              <div className="or-separator">
                <span className="line"></span>
                <span className="or-text">{t('or')}</span>
                <span className="line"></span>
              </div>

              <div className="google-login">
                {hasFunctionalConsent ? (
                  <GoogleOAuthProvider clientId={config.google.clientId}>
                    <GoogleLogin
                      onSuccess={handleGoogleLoginSuccess}
                      onError={() => {
                        setError('Google Sign-In Failed');
                      }}
                    />
                  </GoogleOAuthProvider>
                ) : (
                  <button
                    className="btn_google_consent_placeholder"
                    type="button"
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      borderRadius: '4px',
                      border: '1px solid #dadce0',
                      backgroundColor: '#ffffff',
                      color: '#3c4043',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                    }}
                    onClick={() => {
                      const event = new CustomEvent('open-cookie-banner');
                      window.dispatchEvent(event);
                    }}
                  >
                    <FaGoogle style={{ marginRight: '8px', color: '#4285F4', fontSize: '16px' }} />
                    {t('enable_google_login_cookies') !== 'enable_google_login_cookies'
                      ? t('enable_google_login_cookies')
                      : 'Sign in with Google (Accept Cookies)'}
                  </button>
                )}
              </div>

              <div className="microsoft-login">
                <button className="btn_form" type="button" onClick={handleMicrosoftLogin}>
                  {t('continue_with_microsoft_365')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
